import {
    BehaviorSubject,
    forkJoin,
    Observable,
    Subject,
    throwError as observableThrowError,
} from 'rxjs';

import {
    catchError,
    distinctUntilChanged,
    filter,
    finalize,
    mapTo,
    take,
    tap,
} from 'rxjs/operators';
import { Injectable, NgZone } from '@angular/core';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import {
    Organization,
    ReducedOrganization,
} from 'app/user-account/user-organization.model';
import { SUBSCRIPTION_STATE, User } from '../user-account/user.model';
import { BEACONSTAC_HOSTS } from 'app/app.constants';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { SsoAuthService } from './sso.service';
import { isBeaconstacDomainRequest, PRODUCT_TYPES } from '../shared/utils';
import { DeviceDetectorService } from 'ngx-device-detector';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { addDays } from 'date-fns';
import { IndexedDbService } from './indexed-db.service';
import { OBJECT_STORES } from '../index-db';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as LDClient from 'launchdarkly-js-client-sdk';

@Injectable()
export class AuthService {
    static tokeTypes = {
        token: 'Token',
        bearer: 'Bearer',
    };
    private static storageKeys = {
        token: 'BSAuthToken',
        tokenType: 'BSAuthTokenType',
        user: 'BSUserObj',
        isSafeSuperUser: 'BSIsSafeSuperUser',
        currentOrgObj: 'BSCurrentOrgObj',
        productType: 'ProductType',
    };
    private static cookieKeys = {
        session: 'BSSessionToken',
    };
    public isLoggedInSubject = new BehaviorSubject<boolean>(false);
    public isLoggedIn$ = this.isLoggedInSubject.pipe(distinctUntilChanged());
    // Auth0 Observables
    public isAuthenticated$;
    public authError$: any;
    public postAuthtargetRoute$;
    private tokenSubject = new BehaviorSubject<string>('');
    private tokenTypeSubject = new BehaviorSubject<string>('');
    private userSubject = new BehaviorSubject<User>(null);
    public user$ = this.userSubject.pipe(distinctUntilChanged());
    private isSafeSuperUserSubject = new BehaviorSubject<boolean>(false);
    private currentOrgIdSubject = new BehaviorSubject<any>(null);
    private organizationsListSubject = new BehaviorSubject<Organization[]>([]);
    private currentOrgCreatedDate = new BehaviorSubject<any>(null);
    public currentOrgId$ = this.currentOrgIdSubject.pipe(
        distinctUntilChanged()
    );

    private readonly isMobileDevice: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(false);
    public isMobileDevice$ = this.isMobileDevice.pipe(distinctUntilChanged());
    // Local Storage Changes Subject for multiple inter tab communication
    private readonly storageChangeSubject: Subject<StorageEvent> =
        new Subject<StorageEvent>();
    storageChange$: Observable<StorageEvent> = this.storageChangeSubject.pipe();
    private isOpenedAsPWASubject = new BehaviorSubject<boolean>(false);
    public isOpenedAsPWA$ = this.isOpenedAsPWASubject.asObservable();

    // LaunchDarkly client instance
    private LDClient: LDClient.LDClient = null;
    private launchDarklyReadySubject = new BehaviorSubject<boolean>(false);
    public launchDarklyReady$ = this.launchDarklyReadySubject.pipe(distinctUntilChanged());

    private checkDeviceOsSubject = new BehaviorSubject<string>('');
    public checkDeviceOs$ = this.checkDeviceOsSubject.asObservable();

    constructor(
        private http: HttpClient,
        private router: Router,
        private cookieService: CookieService,
        private ssoService: SsoAuthService,
        private deviceDetectorService: DeviceDetectorService,
        private indexedDBService: IndexedDbService,
        private ngZone: NgZone
    ) {
        this.detectDevice();
        this.initializeMonitoring();
        this.user$.pipe(takeUntilDestroyed()).subscribe((changedUser) => {
            try {
                const isSafeSuperUser = localStorage.getItem(
                    AuthService.storageKeys.isSafeSuperUser
                );
                if (isSafeSuperUser) {
                    this.setSafeSuperUser(JSON.parse(isSafeSuperUser));
                }
            } catch (e) {}
            if (
                changedUser &&
                this.checkSubscriptionState(changedUser) &&
                !this.isSafeSuperUser()
            ) {
                const urlQueryParams = new URLSearchParams(
                    window.location.search
                );
                const queries = {
                    source: 'expiredplan-page-upgrade-cta',
                };
                // @ts-ignore
                for (const param of urlQueryParams) {
                    queries[param[0]] = param[1];
                }
                this.router.navigate(['/renew-plan'], { queryParams: queries });
            }
        });
        this.isAuthenticated$ = this.ssoService.isAuthenticatedUser$;
        this.authError$ = this.ssoService.error$;
        this.postAuthtargetRoute$ = this.ssoService.targetRoute$;

        this.isAuthenticated$
            .pipe(
                distinctUntilChanged(),
                filter((isAuthenticated) => isAuthenticated === true)
            )
            .subscribe((res) => {
                this.ssoService.getToken$().subscribe((authToken) => {
                    try {
                        this.tokenSubject.next(authToken);
                        this.tokenTypeSubject.next(
                            AuthService.tokeTypes.bearer
                        );
                        if (res) {
                            this.fetchOrganizationAndUser().subscribe(
                                () => {},
                                (err) => {
                                    console.log(err);
                                    if (!navigator.onLine) {
                                        this.router.navigate(['/cards']);
                                    } else {
                                        this.router.navigate(['/sso']);
                                        this.ssoService.errorSubject$.next({
                                            message:
                                                'No user account found. Please contact <a href="mailto:support@uniqode.com">support@uniqode.com</a>.',
                                        });
                                    }
                                }
                            );
                        } else {
                            // Not authenticated
                            this.indexedDBService
                                .getAll(OBJECT_STORES.USERS)
                                .pipe(take(1))
                                .subscribe((users) => {
                                    if (users.length > 0) {
                                        const user = new User(users[0]);
                                        this.setUser(user);
                                    }
                                });
                        }
                    } catch (e) {
                        console.error(e);
                    }
                });
            });

        this.detectOs();
    }

    private static get authUrl() {
        let requestURL =
            environment.baseURL +
            environment.apiEndpoint +
            environment.apiVersion;

        if (isBeaconstacDomainRequest()) {
            requestURL = requestURL.replace(
                environment.baseURL,
                environment.apiBaseURL
            );
        }
        return requestURL;
    }

    private static clearLocalStorage() {
        for (const key in AuthService.storageKeys) {
            localStorage.removeItem(AuthService.storageKeys[key]);
        }
    }

    private static getNewSessionId(): string {
        let id = '';
        const possible =
            'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@#$!';

        for (let i = 0; i < 20; i++) {
            id += possible.charAt(Math.floor(Math.random() * possible.length));
        }
        return id;
    }

    initializeMonitoring() {
        datadogRum.init({
            applicationId: environment.DDApplicationId,
            clientToken: environment.DDClientToken,
            // `site` refers to the Datadog site parameter of your organization
            // see https://docs.datadoghq.com/getting_started/site/
            site: environment.DDSite,
            service: environment.DDService,
            env: environment.envName,
            // Specify a version number to identify the deployed version of your application in Datadog
            // version: '1.0.0',
            sessionSampleRate: 100,
            sessionReplaySampleRate: 100,
            trackUserInteractions: true,
            trackResources: true,
            trackLongTasks: true,
            defaultPrivacyLevel: 'mask-user-input',
        });

        datadogLogs.init({
            clientToken: environment.DDClientToken,
            site: environment.DDSite,
            service: environment.DDService,
            env: environment.envName,
            forwardErrorsToLogs: true,
            sessionSampleRate: 100,
        });

        try {
            datadogRum.startSessionReplayRecording();
        } catch (e) {
            console.error(e);
        }
    }

    detectDevice() {
        this.isMobileDevice.next(this.deviceDetectorService.isMobile());
    }

    updateOrg() {
        const org = JSON.parse(
            localStorage.getItem(AuthService.storageKeys.currentOrgObj)
        );
        if (org) {
            this.setCurrentOrgObjInLocalStorage(org);
        }
    }

    fetchOrganizationAndUser() {
        return forkJoin([this.fetchOrganization(), this.fetchUser()]).pipe(
            tap((res) => {
                this.setOrganizationsList(res[0]['results']);
                const user = new User(res[1]);
                this.updateOrg();
                this.setUser(user);
                this.addUserToIndexDb(user);
                this.setupExternalServices();
                this.isLoggedInSubject.next(true);
            })
        );
    }

    addUserToIndexDb(user: User) {
        try {
            this.ngZone.run(() => {
                this.indexedDBService
                    .clearAndAdd(OBJECT_STORES.USERS, user.toJSON())
                    .pipe()
                    .subscribe({
                        next: () => {
                            console.info('User saved in index DB');
                        },
                        error: (err) => {
                            console.error('Error saving User in index DB', err);
                            if (err.target && err.target.error) {
                                console.error(
                                    'IndexedDB Error:',
                                    err.target.error
                                );
                            }
                        },
                    });
            });
        } catch (err) {
            console.log(err);
        }
    }

    fetchUser(): Observable<any> {
        const argsObject = {};
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `${this.getTokenType()} ${this.getToken()}`,
        });
        argsObject['headers'] = headers;
        return this.http
            .get(AuthService.authUrl + '/users/me/', argsObject)
            .pipe(
                catchError((response: any): Observable<any> => {
                    const error = {
                        status: response.status,
                        message: 'Login failed',
                        multiAuthToken: null,
                        user: null,
                    };
                    try {
                        switch (response.status) {
                            case 400:
                                error.message = 'Incorrect credentials';
                                break;
                            case 401:
                                error.message = response.error.detail;
                                break;
                            case 402:
                                const json = response.error;
                                const user = new User(json.user);
                                error.user = user;
                                try {
                                    localStorage.setItem(
                                        AuthService.storageKeys.user,
                                        user.toString()
                                    );
                                } catch (ex) {
                                    console.log(ex);
                                }
                                error.message =
                                    'Your access has expired. Please contact the owner of this website';
                                error.multiAuthToken =
                                    response['multi_auth_token'];
                                break;
                        }
                    } catch (ex) {
                        console.error(ex);
                        error.message = 'Login failed';
                    }
                    return observableThrowError(() => error);
                })
            );
    }

    auth0Login(redirectPath?: string): void {
        this.ssoService.login(redirectPath);
    }

    setupExternalServices() {
        this.setUpdateUserSession();
        this.setUpDataDogUser();
        this.initializeLaunchDarkly();
    }

    setUpDataDogUser() {
        const user = this.getUser();
        datadogRum.setUser({
            id: user.id.toString(),
        });
        this.setDatadogUserProperties(
            user?.meta?.industry,
            user?.meta?.company_size
        );
    }

    setDatadogUserProperties(industry: string, companySize: string) {
        const plan = this.getUser().getReadablePlan();
        const dbcPlan = this.getUser().getReadablePlan(PRODUCT_TYPES.DBC);
        const trial = this.getUser().isOnTrialPlan(PRODUCT_TYPES.QR);
        const dbcTrial = this.getUser().isOnTrialPlan(PRODUCT_TYPES.DBC);
        const group = this.getUser().getReadableGroup();

        if (plan) {
            datadogRum.setUserProperty('plan', plan);
        }
        if (dbcPlan) {
            datadogRum.setUserProperty('dbcPlan', dbcPlan);
        }
        if (industry) {
            datadogRum.setUserProperty('industry', industry);
        }
        if (companySize) {
            datadogRum.setUserProperty('companySize', companySize);
        }
        if (typeof trial === 'boolean') {
            datadogRum.setUserProperty('trial', trial);
        }
        if (typeof dbcTrial === 'boolean') {
            datadogRum.setUserProperty('dbcTrial', dbcTrial);
        }
        if (group) {
            datadogRum.setUserProperty('group', group);
        }
    }

    // LaunchDarkly integration
    private initializeLaunchDarkly() {
        const context: LDClient.LDContext = {
            kind: 'user',
            key: this.getUser().id.toString(),
            custom: {
                plan: this.getUser().getReadablePlan(),
                trial: this.getUser().isOnTrialPlan(PRODUCT_TYPES.QR),
                dbcPlan: this.getUser().getReadablePlan(PRODUCT_TYPES.DBC),
                dbcTrial: this.getUser().isOnTrialPlan(PRODUCT_TYPES.DBC),
                group: this.getUser().getReadableGroup(),
                industry: this.getUser().meta.industry,
                companySize: this.getUser().meta.company_size,
                organizationId: this.getCurrentOrganization().id.toString(),
                parentOrganizationId: this.getCurrentOrganization().parent ? this.getCurrentOrganization().parent.toString() : null,
                organizationName: this.getCurrentOrganization().name,
                userEmailDomain: this.getUser().email.split('@')[1],
                date_joined: this.getUser().date_joined ? new Date(this.getUser().date_joined).getTime() : null
            }
        };
        this.LDClient = LDClient.initialize(environment.launchDarklyClientID, context);
        this.LDClient.on('ready', () => {
            this.launchDarklyReadySubject.next(true);
        });
    }

    public async getFeatureFlag(flagKey: string, defaultValue: boolean | string | number = false) {
        try {
            await this.LDClient.waitForInitialization(5);
            return this.LDClient.variation(flagKey, defaultValue);
        } catch (e) {
            console.error(e);
            return defaultValue;
        }
    }

    logout() {
        AuthService.clearLocalStorage();
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `${this.getTokenType()} ${this.getToken()}`,
        });
        this.http
            .post(AuthService.authUrl + '/users/logout/', null, {
                headers: headers,
            })
            .pipe(
                mapTo(true),
                catchError((error) => {
                    console.error(error);
                    return observableThrowError(() => 'Logout failed');
                }),
                finalize(() => {
                    this.completeLogoutProcess();
                })
            )
            .subscribe();
    }

    completeLogoutProcess() {
        const logoutURL = this.getUser()?.organization.meta['logout_url'];
        this.setSafeSuperUser(null);
        this.setUser(null);
        this.setCurrentOrgObjInLocalStorage(null);
        this.tokenSubject.next(null);
        this.isLoggedInSubject.next(false);
        this.clearUserSession();

        if (this.isWhiteLabelDashboard()) {
            this.router.navigate(['/login']);
        } else {
            this.ssoService.logout(logoutURL);
            this.router.navigate(['/logout']);
        }
    }

    isLoggedIn() {
        return this.tokenSubject.getValue() !== null;
    }

    setUser(user: User) {
        this.user$.pipe(take(1)).subscribe((res) => {
            if (JSON.stringify(user) === JSON.stringify(res)) {
                return;
            }
            this.userSubject.next(user);
            if (user) {
                try {
                    localStorage.setItem(
                        AuthService.storageKeys.user,
                        user.toString()
                    );
                } catch (ex) {
                    console.log(ex);
                }
                if (!this.getCurrentOrganization().id) {
                    this.setCurrentOrgObjInLocalStorage(user.organization);
                }
            } else {
                try {
                    localStorage.removeItem(AuthService.storageKeys.user);
                } catch (ex) {
                    console.log(ex);
                }
            }
        });
    }

    setCurrentOrgObjInLocalStorage(organization: Organization) {
        if (organization) {
            try {
                localStorage.setItem(
                    AuthService.storageKeys.currentOrgObj,
                    JSON.stringify(organization)
                );
                this.currentOrgIdSubject.next(organization.id);
                this.currentOrgCreatedDate.next(organization.created);
                this.indexedDBService
                    .clearAndAdd(OBJECT_STORES.ORGANIZATIONS, organization)
                    .pipe()
                    .subscribe();
            } catch (ex) {
                console.log(ex);
            }
        } else {
            try {
                localStorage.removeItem(AuthService.storageKeys.currentOrgObj);
                this.currentOrgIdSubject.next(null);
                this.currentOrgCreatedDate.next(null);
            } catch (ex) {
                console.log(ex);
            }
        }
    }

    getToken() {
        return this.tokenSubject.getValue();
    }

    getTokenType() {
        return this.tokenTypeSubject.getValue();
    }

    getUser() {
        return this.userSubject.getValue();
    }

    isSuperAdmin() {
        return this.getUser()?.isSuperAdmin() ? true : false;
    }

    getOrganization() {
        return this.getUser().isSuperAdmin()
            ? this.getUser().organization
            : this.getCurrentOrganization();
    }

    setOrganizationsList(res) {
        const orgList = [];
        for (const org of res) {
            orgList.push(new Organization(org));
        }
        this.organizationsListSubject.next(orgList);
    }

    getCurrentOrganization(): Organization {
        return new Organization(
            JSON.parse(
                localStorage.getItem(AuthService.storageKeys.currentOrgObj)
            )
        );
    }

    getCurrentOrgId() {
        return this.currentOrgIdSubject.getValue();
    }

    // Returns if the dashboard is white labeled or not
    isWhiteLabelDashboard() {
        const host = window.location.hostname.toLowerCase();
        return (
            BEACONSTAC_HOSTS.indexOf(host) === -1 &&
            host.indexOf('beaconstac.com') === -1 &&
            host.indexOf('uniqode.com') === -1 &&
            host.indexOf('amplifyapp.com') === -1
        );
    }

    setUpdateUserSession(): void {
        if (this.hasUserSessionExpired()) {
            const expires = addDays(new Date(), 1);
            this.cookieService.set(
                AuthService.cookieKeys.session,
                AuthService.getNewSessionId(),
                new Date(Number.parseInt(String(expires.getTime())))
            );
        }
    }

    clearUserSession(): void {
        this.cookieService.deleteAll(AuthService.cookieKeys.session);
        this.cookieService.deleteAll('domain');
    }

    hasUserSessionExpired(): boolean {
        return !this.cookieService.get(AuthService.cookieKeys.session);
    }

    isSafeSuperUser() {
        return this.isSafeSuperUserSubject.getValue();
    }

    fetchOrganization(): Observable<any> {
        const argsObject = {};
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `${this.getTokenType()} ${this.getToken()}`,
        });
        argsObject['headers'] = headers;
        return this.http
            .get(
                AuthService.authUrl + '/myorganizations/?default=true',
                argsObject
            )
            .pipe(
                catchError((response: any): Observable<any> => {
                    return observableThrowError(response);
                })
            );
    }

    checkSubscriptionState(user: User) {
        if (
            (user.subscription.qr &&
                user.subscription.qr.state !== SUBSCRIPTION_STATE.Expired) ||
            (user.subscription.dbc &&
                user.subscription.dbc.state !== SUBSCRIPTION_STATE.Expired)
        ) {
            return false;
        }

        return true;
    }

    private setSafeSuperUser(is_safe_super_user: boolean) {
        this.isSafeSuperUserSubject.next(is_safe_super_user);
        if (is_safe_super_user) {
            try {
                localStorage.setItem(
                    AuthService.storageKeys.isSafeSuperUser,
                    is_safe_super_user.toString()
                );
            } catch (ex) {
                console.log(ex);
            }
        } else {
            try {
                localStorage.removeItem(
                    AuthService.storageKeys.isSafeSuperUser
                );
            } catch (ex) {
                console.log(ex);
            }
        }
    }

    getCurrentReducedOrganization(): ReducedOrganization {
        const organization = this.getCurrentOrganization();
        return {
            id: organization.id,
            name: organization.name,
            master: !organization.parent,
            beacon_count: organization.beacon_count,
        };
    }

    getOrgByID(id: number): Organization {
        return this.organizationsListSubject
            .getValue()
            ?.filter((res) => res.id === id)[0];
    }

    appendOrgToList(currentOrg) {
        const orgList = [];
        orgList.push(currentOrg);
        for (const org of this.organizationsListSubject.getValue()) {
            orgList.push(new Organization(org));
        }
        this.organizationsListSubject.next(orgList);
    }

    appendOrganizationsToList(orgsList) {
        let list = [];
        list = this.organizationsListSubject.getValue();
        list = list.concat(orgsList);
        this.organizationsListSubject.next(list);
    }

    detectOs() {
        this.checkDeviceOsSubject.next(
            this.deviceDetectorService.getDeviceInfo().os
        );
    }

    public setPwaState(isPWA: boolean) {
        this.isOpenedAsPWASubject.next(isPWA);
    }
}
